import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, BIZ } from "../lib/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed top-0 inset-x-0 z-50 bg-dst-ink border-b border-white/10 shadow-lg"
      >
        <div className="container-dst flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`relative text-sm font-manrope font-600 tracking-wide uppercase transition-colors duration-300 ${
                  pathname === item.to ? "text-dst-red" : "text-white/80 hover:text-white"
                }`}
              >
                {item.label}
                {pathname === item.to && (
                  <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-0.5 bg-dst-red" />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={BIZ.phoneHref}
              data-testid="nav-call"
              className="flex items-center gap-2 text-sm font-oswald uppercase tracking-wider text-white/80 hover:text-white transition-colors"
            >
              <Phone size={16} className="text-dst-red" /> {BIZ.phone}
            </a>
            <a
              href={BIZ.bookUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-book"
              className="btn-dst bg-dst-red text-white hover:bg-white hover:text-dst-ink"
            >
              Book Now
            </a>
          </div>

          <button
            data-testid="menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-dst-ink pt-24 px-6 lg:hidden"
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="block py-4 border-b border-white/10 font-oswald text-3xl uppercase tracking-tight text-white"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <a href={BIZ.bookUrl} target="_blank" rel="noreferrer" data-testid="mobile-nav-book" className="btn-dst bg-dst-red text-white w-full">Book Now</a>
              <a href={BIZ.phoneHref} className="btn-dst border border-white/30 text-white w-full">
                <Phone size={16} /> {BIZ.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
